/**
 * LECTOR DE RUEDA — consultas sobre el catálogo declarativo (`recetas-agnosticas.js`).
 *
 * Funciones puras: una receta (+ tema para color) → datos listos para dibujar.
 * Una apuesta es `{sectores:[...], multiplicador}`; gana si el sector ganador
 * está en `sectores`. Las consultas de grupo (bloques, cero, familia) viven aquí
 * una vez; los consumidores las llaman, no las reimplementan.
 *
 * `shared/` es ESM; el backend lo importa con `require` (Node 22).
 */

// ─── SECTORES: la rueda física (lo que el wheel pinta) ────────────────────────

/**
 * Geometría angular de cada sector. El arco se DERIVA del `peso` (esto es
 * dibujo, no negocio: el renderer sabe convertir peso→grados). Devuelve cada
 * sector con `arcInicio`/`arcFin`/`arcGrados` ya calculados.
 *
 * @param {Object} receta - una rueda del catálogo.
 * @returns {Array} sectores con geometría angular (no muta el catálogo).
 */
export function sectoresConArco(receta) {
    const pesoTotal = receta.sectores.reduce((acc, s) => acc + (s.peso || 1), 0);
    let acumulado = 0;
    return receta.sectores.map(s => {
        const peso = s.peso || 1;
        const arcGrados = 360 * (peso / pesoTotal);
        const sector = { ...s, arcInicio: acumulado, arcFin: acumulado + arcGrados, arcGrados };
        acumulado += arcGrados;
        return sector;
    });
}

/**
 * Variante que agrega `esCero` (boolean) e `id` (indice 0-based en la rueda)
 * a cada sector, ademas de los arcs. Es el shape rico que consume el money-path
 * (`rueda-config-factory.js` lo lee como `sectoresUniformesDeLaRueda`). Un
 * sector se considera "fuera de grilla" (esCero) si declara `top != null` —
 * la receta marca asi los sectores LOTO/X/etc. que no entran a la grilla
 * numerica.
 *
 * @param {Object} receta - una rueda del catálogo.
 * @returns {Array} sectores con {id, arcGrados, arcInicio, arcFin, esCero, ...originales}.
 */
export function sectoresConArcoYesCero(receta) {
    return sectoresConArco(receta).map((s, indice) => ({
        ...s,
        id: indice,
        esCero: s.top != null,
    }));
}

// ─── APUESTAS: paytable / stats / liquidación ─────────────────────────────────
//
// Una receta trae UN array `apuestas[]`. Cada apuesta es FIJA (trae `sectores`)
// o GEOMÉTRICA (trae `aridad`, sin `sectores` — el jugador la arma en el mat).
// Estos helpers leen ese array. `apuestasFijas` = las que tienen `sectores`

/** Todas las apuestas FIJAS (con `sectores`): color, docenas, bloques, cero, loto... */
export function apuestasFijas(receta) {
    return receta.apuestas.filter(a => Array.isArray(a.sectores));
}

/** Todas las apuestas GEOMÉTRICAS (con `aridad`, sin `sectores`). */
export function apuestasGeometricas(receta) {
    return receta.apuestas.filter(a => a.aridad != null && !Array.isArray(a.sectores));
}

/**
 * ¿La receta declara una apuesta con este id (activa)? Helper booleano para
 * consumers que solo necesitan saber "si esta o no".
 */
export function apuestaOfrecidaEnReceta(receta, idApuesta) {
    if (!receta || !Array.isArray(receta.apuestas)) return false;
    return receta.apuestas.some(a => a.id === idApuesta && a.activa !== false);
}

/**
 * ¿La receta declara al menos una apuesta de este grupo (activa)? Helper
 * booleano analogo a `apuestaOfrecidaEnReceta` pero para categorias
 * (`color`/`paridad`/`bloques`/`columnas`/`tercios`/`espejos`/`gemelos`/`duo`/
 * `linea`/`cuadra`/`dobleLinea`).
 */
export function categoriaOfrecidaEnReceta(receta, grupo) {
    if (!receta || !Array.isArray(receta.apuestas)) return false;
    return receta.apuestas.some(a => a.grupo === grupo && a.activa !== false);
}

/**
 * ¿La apuesta se ofrece en este canal? Si declara `canales`, el canal pedido
 * debe estar en la lista; si no, vale para todos. Sin canal pedido, no filtra.
 */
function ofrecidaEnCanal(apuesta, canal) {
    if (canal == null || !Array.isArray(apuesta.canales)) return true;
    return apuesta.canales.includes(canal);
}

function ofrecida(apuesta, idsHabilitados, canal) {
    return (
        apuesta.activa !== false &&
        (idsHabilitados === null || idsHabilitados === undefined || idsHabilitados.includes(apuesta.id)) &&
        ofrecidaEnCanal(apuesta, canal)
    );
}

/** Apuestas FIJAS ofrecidas (activas, en idsHabilitados, y para el canal). */
export function lineasOfrecidas(receta, idsHabilitados, canal) {
    return apuestasFijas(receta).filter(l => ofrecida(l, idsHabilitados, canal));
}

/** Apuestas GEOMÉTRICAS ofrecidas (activas, en idsHabilitados, y para el canal). */
export function patronesOfrecidos(receta, idsHabilitados, canal) {
    return apuestasGeometricas(receta).filter(p => ofrecida(p, idsHabilitados, canal));
}

/** Apuestas fijas de una categoría (inside/outside). */
export function lineasDeCategoria(receta, categoria) {
    return apuestasFijas(receta).filter(l => l.categoria === categoria);
}

/** Apuesta fija por id exacto, o null. */
export function linea(receta, id) {
    return apuestasFijas(receta).find(l => l.id === id) || null;
}

/** Sectores de una apuesta fija por id (vacío si no existe o no es fija). */
export function sectoresDeLinea(receta, id) {
    const l = linea(receta, id);
    return l ? l.sectores : [];
}

/**
 * Mapa `{idDeApuesta: sectores}` de todas las apuestas fijas de un grupo de
 * la receta. Reemplaza a `familiaIndexada` y `bloques` — agnostico: una
 * receta nueva con `grupo: 'pais'` devuelve `{argentina: [...], brasil: [...]}`
 * sin tocar codigo. La clave es el id literal de la apuesta (la receta lo
 * declara), no una letra/numero extraido por prefijo.
 */
export function apuestasPorGrupo(receta, grupo) {
    const out = {};
    for (const a of apuestasFijas(receta)) {
        if (a.grupo === grupo) out[a.id] = a.sectores;
    }
    return out;
}

/**
 * Devuelve la clave i18n del color de una apuesta segun el tema activo.
 * La apuesta declara su `id` estable (money-path). El tema decide como se llama
 * ese color visualmente: en CASINO_ROYALE la paleta-0 es 'red', en HALLOWEEN
 * la misma paleta-0 es 'orange'. Asi cambiar de tema actualiza visual + label
 * sin tocar receta ni backend.
 */
export function nombreI18nDePaletaDeApuesta(apuesta, receta, theme) {
    const valorSector = apuesta?.sectores?.[0];
    if (valorSector == null) return null;
    const sec = sector(receta, valorSector);
    const paletaKey = sec?.paleta;
    if (!paletaKey) return null;
    return theme?.paletas?.[paletaKey]?.nombreI18nKey ?? null;
}

/**
 * Sectores que se renderizan en la fila SUPERIOR del mat (los "ceros" /
 * sectores con `top != null`). El resolver geometrico del mat lo usa
 * para mapear coordenada X del toque -> qué cero esta sobre ese punto
 * (50/50 si son 2, 33/33/33 si son 3, etc.). Orden = orden de aparicion
 * en `receta.sectores` (mismo que el factory pinta).
 */
export function cerosDelSuperior(receta) {
    const sectores = Array.isArray(receta?.sectores) ? receta.sectores : [];
    const out = [];
    for (let i = 0; i < sectores.length; i++) {
        if (sectores[i].top != null) out.push(sectores[i]);
    }
    return out;
}

/**
 * Devuelve el id de la apuesta de la receta que cubre EXACTAMENTE el sector
 * dado (aridad efectiva 1 sobre ese valor). Lo usa el resolver geometrico
 * del mat para mapear "click en sector N" -> id de apuesta nombrada.
 *   - mundial sector 4 -> 'mexico' (declarada con sectores:[4], mult propio)
 *   - european sector 7 -> null (no hay apuesta exclusiva, cae al comodin
 *     `aciertoDirecto` con aridad 1)
 * Cero hardcodes: el id sale de la receta, el multiplicador tambien.
 */
export function idDeApuestaQueCubreSector(receta, valor) {
    const apuestas = Array.isArray(receta?.apuestas) ? receta.apuestas : [];
    // 1. Apuesta exclusiva del sector (aridad 1): tiene prioridad sobre cualquier
    //    agrupada. Ej: mundial sector 4 → 'mexico'.
    for (let i = 0; i < apuestas.length; i++) {
        const a = apuestas[i];
        if (a.activa === false) continue;
        if (Array.isArray(a.sectores) && a.sectores.length === 1 && a.sectores[0] === valor) {
            return a.id;
        }
    }
    // 2. Si el sector es del superior (top != null), buscar una apuesta
    //    multi-sector inside que cubra EXACTAMENTE el conjunto de sectores
    //    del superior. Caso típico: rueda10_american con apuesta `loto[0,11]`
    //    cubriendo los dos sectores LOTO — el botón visual es uno solo y
    //    tapearlo debe emitir la apuesta agrupada, no pleno del 0 o del 11.
    //    Si el botón no agrupa (european: un solo LOTO sin apuesta `loto`
    //    declarada), esta rama no encuentra match y devuelve null → caller
    //    cae al wildcard `aciertoDirecto`.
    const sectores = Array.isArray(receta?.sectores) ? receta.sectores : [];
    const sectorTocado = sectores.find(s => s.valor === valor);
    if (sectorTocado && sectorTocado.top != null) {
        const valoresDelSuperior = [];
        for (let i = 0; i < sectores.length; i++) {
            if (sectores[i].top != null) valoresDelSuperior.push(sectores[i].valor);
        }
        if (valoresDelSuperior.length > 1) {
            for (let i = 0; i < apuestas.length; i++) {
                const a = apuestas[i];
                if (a.activa === false) continue;
                if (a.categoria !== 'inside') continue;
                if (!Array.isArray(a.sectores)) continue;
                if (a.sectores.length !== valoresDelSuperior.length) continue;
                // Set-equality entre sectores de la apuesta y sectores del superior.
                const incluyeTodos = valoresDelSuperior.every(v => a.sectores.indexOf(v) !== -1);
                if (incluyeTodos) return a.id;
            }
        }
    }
    return null;
}

/** Ids de las apuestas-conjunto que contienen al sector. */
export function lineasDelSector(receta, valor) {
    return apuestasFijas(receta)
        .filter(l => l.sectores.includes(valor))
        .map(l => l.id);
}

/** El sector por su valor, o null. */
export function sector(receta, valor) {
    return receta.sectores.find(s => s.valor === valor) || null;
}

/** ¿El sector pertenece a la línea `id`? Consulta general — `cero` no es especial. */
export function sectorPerteneceA(receta, valor, id) {
    return sectoresDeLinea(receta, id).includes(valor);
}

// ─── LIQUIDACIÓN (money-path) ─────────────────────────────────────────────────

/** ¿La línea cubre al sector ganador? */
export function lineaCubre(linea, sectorGanador) {
    return linea.sectores.includes(sectorGanador);
}

// ─── MAT: botones del tapete ──────────────────────────────────────────────────

/** Botones del mat cuya línea se ofrece en el canal. */
export function botonesOfrecidos(receta, idsHabilitados) {
    const ofrecidas = new Set(lineasOfrecidas(receta, idsHabilitados).map(l => l.id));
    return receta.mat.botones.filter(b => ofrecidas.has(b.linea));
}

/**
 * El número base de una celda de la grilla (aritmética de grilla, dibujo puro).
 * `fila`/`col` 0-based. Devuelve el valor del sector en esa celda según el orden
 * de la grilla del mat. Las adyacencias (duo=base+1/base+cols, cuarteto, ...) se
 * derivan de esto — NO son datos.
 */
export function valorEnCelda(receta, fila, col) {
    return fila * receta.mat.columnas + col + 1;
}

/**
 * Grilla numerica del mat: `{ rows, cols, zerosRow, numbers }`. Lo consumen
 * DragInputController (front-customer/tv) y history-bar (front-customer) para
 * cachear coordenadas y navegar la grilla.
 *
 * Derivado declarativo:
 *   - `cols`     = receta.mat.columnas.
 *   - `numbers`  = cantidad de sectores que entran a la grilla (los NO marcados
 *                  como "fuera de grilla" por `top != null`).
 *   - `rows`     = ceil(numbers / cols). Solo filas numericas (sin superior/
 *                  inferior, mantenido bit-exact con el shape historico).
 *   - `zerosRow` = 1 si la receta tiene boton en seccion 'superior', 0 si no.
 *
 * Recetas sin mat (mundial): devuelve undefined. El caller debe defenderse.
 */
export function matGridDeReceta(receta) {
    const mat = receta && receta.mat;
    if (!mat) return undefined;
    const cols = mat.columnas;
    const botones = mat.botones || [];
    const sectores = Array.isArray(receta.sectores) ? receta.sectores : [];
    let sectoresIndividualesDeGrilla = 0;
    for (let i = 0; i < sectores.length; i++) {
        if (sectores[i].top == null) sectoresIndividualesDeGrilla++;
    }
    let haySeccionSuperior = false;
    for (let i = 0; i < botones.length; i++) {
        if (botones[i].seccion === 'superior') {
            haySeccionSuperior = true;
            break;
        }
    }
    const rows = cols > 0 ? Math.ceil(sectoresIndividualesDeGrilla / cols) : 0;
    return {
        rows,
        cols,
        zerosRow: haySeccionSuperior ? 1 : 0,
        numbers: sectoresIndividualesDeGrilla,
    };
}

/**
 * Geometria de la rueda (radios del wheel renderer): `{ rIn, rMid, rOut,
 * innerBorderWidth? }`. La receta declara nombres en castellano
 * (`radioInterior`/`Medio`/`Exterior`/`anchoBordeInterior`); este helper
 * traduce a los nombres cortos que consumen los renderers.
 *
 * Recetas sin geometria declarada: devuelve null. El caller usa defaults.
 */
export function geometriaDeReceta(receta) {
    const geometria = receta && receta.geometria;
    if (!geometria) return null;
    const geometry = {
        rIn: geometria.radioInterior,
        rMid: geometria.radioMedio,
        rOut: geometria.radioExterior,
    };
    if (geometria.anchoBordeInterior !== undefined) {
        geometry.innerBorderWidth = geometria.anchoBordeInterior;
    }
    return geometry;
}
